import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { companyBlockUnblockSchema, CompanyBlockUnblockValidationSchema } from "@/features/privateCompanies/schemas/blockUnblockCompany.schema";
import useDisableClientCompany from "@/features/privateCompanies/hooks/useDisableClientCompany";
import useEnableClientCompany from "@/features/privateCompanies/hooks/useEnableClientCompany";
import { CompanyData } from "@/features/companies/types/companies.type";
import TextArea from "@/common/components/TextArea";


interface DisableEnableCompanyProps {
  company?: CompanyData;
}

const DisableEnableCompany = ({ company }: DisableEnableCompanyProps) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [blockModalIsOpen, setBlockModalIsOpen] = useState(false);
  const [unblockModalIsOpen, setUnblockModalIsOpen] = useState(false);

  const { mutate: blockEstablishment } = useDisableClientCompany({
    companyId: company?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', company?.id] });
      blockFormReset();
      setBlockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: blockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { mutate: unblockEstablishment } = useEnableClientCompany({
    companyId: company?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', company?.id] });
      unblockFormReset();
      setUnblockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: unblockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isDisabled = company?.status === 'disabled';

  const {
    register: blockFormRegister,
    reset: blockFormReset,
    setError: blockFormSetError,
    handleSubmit: blockFormHandleSubmit,
    formState: { errors: blockFormErrors },
  } = useForm<CompanyBlockUnblockValidationSchema>({
    resolver: zodResolver(companyBlockUnblockSchema),
  });

  const {
    register: unblockFormRegister,
    reset: unblockFormReset,
    setError: unblockFormSetError,
    handleSubmit: unblockFormHandleSubmit,
    formState: { errors: unblockFormErrors },
  } = useForm<CompanyBlockUnblockValidationSchema>({
    resolver: zodResolver(companyBlockUnblockSchema),
  });

  return (
    <>
      <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isDisabled && 'bg-[#FCCDCA33]')}>
        <div className="flex items-center gap-x-2">
          <InfoIcon strokeWidth={1} className="text-yellow-600" />

          <div>
            <p className="font-bold text-[10px] leading-3">
              {!isDisabled ? (
                'Ao desativar, a empresa não poderá realizar transações e todos os usuários perderão o acesso ao sistema.'
              ) : (
                'Reative a empresa para permitir que todas as transações sejam realizadas e o acesso dos usuários restabelecidos.'
              )}
            </p>

            <p className="font-light text-[10px] italic leading-3">
              {!isDisabled ? (
                'Se a empresa for desativado, apenas a Tcard poderá reativar.'
              ) : (
                'Apenas a Tcard pode reativar uma empresa.'
              )}
            </p>
          </div>
        </div>

        {!isDisabled ? (
          <Modal
            Trigger={(
              <Button operationKlassToCheckPermission="Admin::Client::Companies::Disable" type="button" variant="error">
                Desativar empresa
              </Button>
            )}
            title="Você tem certeza?"
            subtitle="Se a empresa for desativada, apenas a Tcard poderá reativar."
            subtitleClassName="mt-1 text-white-300"
            showClose={false}
            useCancellableFooter
            isOpen={blockModalIsOpen}
            setIsOpen={setBlockModalIsOpen}
            onConfirm={blockFormHandleSubmit((data) => blockEstablishment(data))}
            body={(
              <form className="flex flex-col gap-4">
               <TextArea 
                className="text-white-800 opacity-80"
                label="Motivo"
                rows={4} 
                {...blockFormRegister('reason')}
                errorMessage={blockFormErrors.reason?.message || undefined}
               />
              </form>
            )}
          />
        ) : (
          <Modal
            Trigger={(
              <Button operationKlassToCheckPermission="Admin::Client::Companies::Enable" type="button">
                Reativar empresa
              </Button>
            )}
            title="Você tem certeza?"
            subtitle="Se a empresa for reativada, todas as transações poderão ser realizadas."
            subtitleClassName="mt-1 text-white-300"
            showClose={false}
            useCancellableFooter
            isOpen={unblockModalIsOpen}
            setIsOpen={setUnblockModalIsOpen}
            onConfirm={unblockFormHandleSubmit((data) => unblockEstablishment(data))}
            body={(
              <form className="flex flex-col gap-4">
               <TextArea 
                className="text-white-800 opacity-80"
                label="Motivo"
                rows={4} 
                {...unblockFormRegister('reason')}
                  errorMessage={unblockFormErrors.reason?.message || undefined}
               />
              </form>
            )}
          />
        )}
      </div>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default DisableEnableCompany;
